"use strict";
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (Object.hasOwnProperty.call(mod, k)) result[k] = mod[k];
    result["default"] = mod;
    return result;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const React = __importStar(require("react"));
const react_beautiful_dnd_1 = require("react-beautiful-dnd");
const ActionMenu_1 = __importDefault(require("../ActionMenu/ActionMenu"));
const Small_1 = __importDefault(require("../Typography/Small"));
const styled_1 = __importDefault(require("../utils/styled"));
const Icon_1 = require("../Icon");
const useUniqueId_1 = require("../useUniqueId");
const utils_1 = require("../utils");
const Container = styled_1.default("table")(({ theme, fixedLayout }) => ({
    width: "100%",
    backgroundColor: theme.color.white,
    textAlign: "left",
    borderCollapse: "collapse",
    fontSize: theme.font.size.small,
    fontFamily: theme.font.family.main,
    tableLayout: fixedLayout ? "fixed" : "initial",
}));
const Tr = styled_1.default.tr(({ isDragging, theme, active, draggable, clickable }) => (Object.assign({ height: 50, display: isDragging ? "table" : "table-row", tableLayout: "fixed", backgroundColor: active ? utils_1.lighten(theme.color.primary, 54) : theme.color.white }, (draggable || clickable
    ? {
        ":hover": {
            backgroundColor: active ? utils_1.lighten(theme.color.primary, 52) : utils_1.lighten(theme.color.primary, 54),
            cursor: clickable ? "pointer" : draggable ? "move" : "default",
        },
    }
    : {}), { ":focus": {
        outline: "none",
        backgroundColor: utils_1.lighten(theme.color.primary, 52),
    }, ".no-focus &:focus": {
        backgroundColor: active ? utils_1.lighten(theme.color.primary, 54) : theme.color.white,
    } })));
const Thead = styled_1.default("thead") `
  tr {
    height: initial;
  }
`;
const Th = styled_1.default("th")(({ theme, sortable }) => (Object.assign({ position: "relative", borderBottom: `1px solid ${theme.color.separators.default}`, color: theme.color.text.dark, paddingBottom: theme.space.base, "&:first-of-type": {
        paddingLeft: theme.space.small,
    }, paddingRight: theme.space.small }, (sortable
    ? {
        ":hover": {
            cursor: "pointer",
            color: theme.color.text.light,
            svg: {
                cursor: "pointer",
                fill: theme.color.text.light,
            },
        },
    }
    : {}))));
const ThContent = styled_1.default("span") `
  display: inline-flex;
  align-items: center;
  height: ${props => props.theme.space.medium}px;
  ${props => props.sorted && `color: ${props.theme.color.text.light};`};
`;
const Td = styled_1.default("td")(({ theme, cellWidth, coloredBorders }) => (Object.assign({ verticalAlign: "middle", borderBottom: "1px solid", color: theme.color.text.default, borderTop: "1px solid", borderColor: coloredBorders ? theme.color.primary : theme.color.border.medium, hyphens: "auto", "&:first-of-type": {
        paddingLeft: theme.space.small,
    }, paddingRight: theme.space.small }, (cellWidth
    ? {
        width: cellWidth,
        wordBreak: "break-all",
        wordWrap: "break-word",
    }
    : {}))));
const Actions = styled_1.default(Td)(({ theme }) => ({
    textAlign: "right",
    paddingRight: theme.space.small,
    /**
     * We use opacity here instead of display: none; or
     * visibility: hidden; because both mess with
     * the box model of the Td while opacity does not.
     */
    opacity: 0,
    "tr:hover &, :hover, tr:focus &, :focus-within, &:focus": {
        opacity: 1,
    },
    "& > div": {
        display: "inline-flex",
    },
}));
const CellIcon = styled_1.default(Td) `
  width: 40px;
  padding: ${props => props.theme.space.base}px;
  color: ${props => props.theme.color.text.lightest};
`;
const ActionLabel = styled_1.default(Small_1.default) `
  color: ${props => props.theme.color.primary};
  margin: 0;
  display: block;
`;
const EmptyView = styled_1.default(Td)(({ theme }) => ({
    color: theme.color.text.default,
    height: 50,
    lineHeight: "50px",
    textAlign: "center",
}));
function Table(_a) {
    var { data = [], columns, onRowClick, rowActionName, rowActions, icon, iconColor, headless, fixedLayout, onReorder, activeRowIndices } = _a, props = __rest(_a, ["data", "columns", "onRowClick", "rowActionName", "rowActions", "icon", "iconColor", "headless", "fixedLayout", "onReorder", "activeRowIndices"]);
    const uid = useUniqueId_1.useUniqueId();
    const standardizedColumns = columns.map(column => {
        if (typeof column === "string") {
            return {
                heading: column,
                cell: (dataEntry) => dataEntry[column],
            };
        }
        else {
            return column;
        }
    });
    const hasIcons = Boolean(data[0] && icon && icon(data[0]));
    const handleKeyDownOnRow = React.useCallback((entry, index) => (e) => {
        if (!onRowClick) {
            return;
        }
        switch (e.key) {
            case "Enter":
                onRowClick(entry, index);
        }
    }, [onRowClick]);
    /**
     * This exists so we don't have to extract <Tr /> further
     * down, which relies on its closure.
     */
    const PseudoDraggable = React.useMemo(() => {
        if (onReorder) {
            return react_beautiful_dnd_1.Draggable;
        }
        return ((props) => React.createElement(React.Fragment, null, props.children({}, {})));
    }, [onReorder]);
    return (React.createElement(react_beautiful_dnd_1.DragDropContext, { onDragEnd: onReorder || (() => { }) },
        React.createElement(Container, Object.assign({ fixedLayout: fixedLayout || Boolean(onReorder) }, props),
            !headless && (React.createElement(Thead, null,
                React.createElement(Tr, { active: false /* It's a heading */ },
                    hasIcons && React.createElement(Th, { key: "-1" }),
                    standardizedColumns.map((column, columnIndex) => (React.createElement(Th, { key: columnIndex, sortable: Boolean(column.onSortClick), onClick: () => column.onSortClick && column.onSortClick(column.sortOrder === "desc" ? "asc" : "desc") },
                        React.createElement(ThContent, { sorted: Boolean(column.sortOrder) },
                            column.heading,
                            column.onSortClick && !column.sortOrder && (React.createElement(Icon_1.ChevronUpDownIcon, { right: true, size: 10, color: "color.border.disabled" })),
                            column.sortOrder &&
                                (column.sortOrder === "desc" ? (React.createElement(Icon_1.ChevronUpIcon, { right: true, size: 10, color: "primary" })) : (React.createElement(Icon_1.ChevronDownIcon, { right: true, size: 10, color: "primary" }))))))),
                    Boolean(rowActions || (onRowClick && rowActionName)) && React.createElement(Th, { key: "infinity" })))),
            React.createElement(react_beautiful_dnd_1.Droppable, { droppableId: uid }, droppableProvided => (React.createElement("tbody", Object.assign({ ref: droppableProvided.innerRef }, droppableProvided.droppableProps), data.length ? (data.map((dataEntry, dataEntryIndex) => {
                /*
                 Because of how border-collapse works, we need a different border color for this TD
                 and the subsequent TD if the current row is "active"
                */
                const shouldTdHaveColoredBorders = activeRowIndices
                    ? activeRowIndices.includes(dataEntryIndex) || activeRowIndices.includes(dataEntryIndex + 1)
                    : false;
                const rowAction = (() => {
                    if (!rowActions) {
                        return null;
                    }
                    const dataEntryRowActions = rowActions(dataEntry);
                    return (React.createElement(Actions, { coloredBorders: shouldTdHaveColoredBorders }, Array.isArray(dataEntryRowActions) ? (React.createElement(ActionMenu_1.default, { items: dataEntryRowActions })) : (dataEntryRowActions)));
                })();
                return (React.createElement(PseudoDraggable, { key: dataEntryIndex, draggableId: String(dataEntryIndex), index: dataEntryIndex }, (provided, snapshot) => (React.createElement(Tr, Object.assign({}, provided.draggableProps, provided.dragHandleProps, { active: activeRowIndices ? activeRowIndices.includes(dataEntryIndex) : false, ref: provided.innerRef, isDragging: Boolean(snapshot.isDragging), onKeyDown: handleKeyDownOnRow(dataEntry, dataEntryIndex), tabIndex: onRowClick ? 0 : undefined, role: onRowClick ? "button" : undefined, draggable: Boolean(onReorder), clickable: Boolean(onRowClick), key: dataEntryIndex, onClick: () => {
                        if (onRowClick) {
                            onRowClick(dataEntry, dataEntryIndex);
                        }
                    } }),
                    hasIcons && (React.createElement(CellIcon, { coloredBorders: shouldTdHaveColoredBorders }, React.createElement(icon(dataEntry), { color: iconColor && iconColor(dataEntry) }))),
                    standardizedColumns.map((column, columnIndex) => (React.createElement(Td, { coloredBorders: shouldTdHaveColoredBorders, cellWidth: column.width, key: columnIndex }, column.cell(dataEntry, dataEntryIndex)))),
                    rowAction,
                    onRowClick && rowActionName && (React.createElement(Actions, { coloredBorders: shouldTdHaveColoredBorders },
                        React.createElement(ActionLabel, null, rowActionName)))))));
            })) : (React.createElement(Tr, { active: false /* It's empty */ },
                React.createElement(EmptyView, { coloredBorders: false, colSpan: columns.length }, "There are no records available")))))))));
}
exports.default = Table;
//# sourceMappingURL=Table.js.map